import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { FaLock, FaExclamationTriangle } from 'react-icons/fa';
import { MdMail } from 'react-icons/md';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const { signInWithEmailAndPassword } = await import('firebase/auth');
      const { auth } = await import('../firebase');

      await signInWithEmailAndPassword(auth, email, password);
      onLogin(); // App.js will handle the state via onAuthStateChanged or this callback
    } catch (error) {
      console.error('Login error:', error);
      let message = 'An error occurred during sign in';

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        message = 'Invalid email or password';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Too many failed attempts. Please try again later.';
      }

      setErrors({ submit: message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container fade-in">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img src="/bunchy-logo.svg" alt="Bunchy Logo" className="logo" />
          </div>
          <h1>Welcome Back</h1>
          <p>Sign in to access your admin panel</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <span style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'flex' }}>
                  <MdMail />
                </span>
              </span>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors({ ...errors, email: '', submit: '' });
                }}
                placeholder="Enter your email"
                className={`form-input ${errors.email ? 'error' : ''}`}
              />
            </div>
            {errors.email && (
              <span className="error-message">
                <FaExclamationTriangle style={{ color: '#ef4444' }} /> {errors.email}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <span className="input-icon">
                <span style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'flex' }}>
                  <FaLock />
                </span>
              </span>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors({ ...errors, password: '', submit: '' });
                }}
                placeholder="Enter your password"
                className={`form-input ${errors.password ? 'error' : ''}`}
              />
            </div>
            {errors.password && (
              <span className="error-message">
                <FaExclamationTriangle style={{ color: '#ef4444' }} /> {errors.password}
              </span>
            )}
          </div>

          {errors.submit && (
            <div className="message error">
              {errors.submit}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-spinner"></span>
            ) : (
              'Sign In'
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="forgot-password-btn"
          >
            Forgot Password?
          </button>

        </form>
      </div>
    </div>
  );
};

export default Login;